import { Injectable } from '@angular/core';
import { IAC_ENVIRONMENT } from '@iac-environment';
import {
  IacRichtextResolver,
  IacSbBlok,
  IacSbDatasourceEntry,
  IacSbVersion,
} from '@iac-storyblok';
import { apiPlugin, ISbStoryData, storyblokInit, useStoryblokBridge } from '@storyblok/js';
import { richTextResolver } from '@storyblok/richtext';
import { BehaviorSubject, combineLatest, filter, first, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StoryblokService {
  private readonly isEditMode$$ = new BehaviorSubject<boolean | undefined>(undefined);
  public readonly isEditMode$ = this.isEditMode$$.pipe(
    filter((isEditMode) => isEditMode !== undefined)
  );

  get isEditMode(): boolean {
    return this.isEditMode$$.getValue() === true;
  }

  private readonly currentStory$$ = new BehaviorSubject<ISbStoryData | null>(null);
  public readonly currentStory$ = this.currentStory$$.pipe(filter((story) => !!story));

  private readonly storyblokSdk = storyblokInit({
    accessToken: IAC_ENVIRONMENT.storyblokToken,
    // apiOptions: {
    //   region: 'eu',
    //   endpoint: `${IAC_ENVIRONMENT.api.baseUrl}/api/v1/storyblok`,
    // },
    use: [apiPlugin],
  });
  private readonly storyblokApi = this.storyblokSdk.storyblokApi;

  private getStoryblokVersion(): IacSbVersion {
    switch (IAC_ENVIRONMENT.env) {
      case 'prod':
        return 'published';
      default:
        return 'draft';
    }
  }

  private decodeToken(encodedToken: string): string {
    return atob(encodedToken);
  }

  private async validatePreviewToken(
    spaceId: string,
    timestamp: string,
    providedToken: string
  ): Promise<boolean> {
    const YOUR_PREVIEW_TOKEN = this.decodeToken(IAC_ENVIRONMENT.storyblokEditModeToken);
    const validationString = `${spaceId}:${YOUR_PREVIEW_TOKEN}:${timestamp}`;

    const encoder = new TextEncoder();
    const data = encoder.encode(validationString);

    const hashBuffer = await crypto.subtle.digest('SHA-1', data);

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const validationToken = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    const isTokenMatch = providedToken === validationToken;
    const currentTimestampSec = Math.floor(Date.now() / 1000);
    const providedTimestampSec = parseInt(timestamp, 10);
    const isTimestampValid = providedTimestampSec > currentTimestampSec - 3600;

    return isTokenMatch && isTimestampValid;
  }

  private listenForEditModeChanges(): void {
    // Parse query params directly from URL to catch them before navigation
    const urlParams = new URLSearchParams(window.location.search);
    const spaceId = urlParams.get('_storyblok_tk[space_id]');
    const timestamp = urlParams.get('_storyblok_tk[timestamp]');
    const providedToken = urlParams.get('_storyblok_tk[token]');

    if (!spaceId || !timestamp || !providedToken) {
      this.isEditMode$$.next(false);
      return;
    }

    this.validatePreviewToken(spaceId, timestamp, providedToken).then((isEditMode) => {
      this.isEditMode$$.next(isEditMode);
    });
  }

  private useStoryblokBridgeInEditMode(): void {
    combineLatest([this.isEditMode$.pipe(filter((isEditMode) => isEditMode)), this.currentStory$])
      .pipe(first())
      .subscribe(([, story]) => {
        useStoryblokBridge(story.id, () => {});
      });
  }

  public initStoryblokBridgeAndEditMode(): void {
    this.listenForEditModeChanges();
    this.useStoryblokBridgeInEditMode();
  }

  private async waitForEditMode(): Promise<boolean> {
    return firstValueFrom(
      this.isEditMode$$.pipe(
        filter((isEditMode): isEditMode is boolean => isEditMode !== undefined)
      )
    );
  }

  private getHeaders(isEditMode: boolean): { headers: { Authorization?: string; token?: string } } {
    if (isEditMode) {
      return { headers: { token: this.decodeToken(IAC_ENVIRONMENT.storyblokPreviewToken) } };
    }
    // const token = this.authService.getToken();
    // if (token) {
    //   return { headers: { Authorization: `Bearer ${token}` } };
    // }
    return { headers: {} };
  }

  public async getStories(slug: string): Promise<ISbStoryData[]> {
    if (this.storyblokApi === undefined) {
      throw new Error('Storyblok API is not initialized');
    }

    const isEditMode = await this.waitForEditMode();

    const stories = await this.storyblokApi.get(
      'cdn/stories',
      {
        version: this.getStoryblokVersion(),
        starts_with: slug,
      },
      this.getHeaders(isEditMode)
    );

    return stories?.data.stories;
  }

  public async fetchStory<Story extends ISbStoryData = ISbStoryData>(slug: string): Promise<Story> {
    if (this.storyblokApi === undefined) {
      throw new Error('Storyblok API is not initialized');
    }

    const isEditMode = await this.waitForEditMode();

    const storyResponse = await this.storyblokApi.get(
      `cdn/stories/${slug}`,
      {
        version: this.getStoryblokVersion(),
        resolve_links: 'url',
        resolve_relations: 'newsTeaser.items',
      },
      this.getHeaders(isEditMode)
    );

    const story = storyResponse?.data.story as Story;
    return story;
  }

  public async getLabel(key: string): Promise<string> {
    if (this.storyblokApi === undefined) {
      throw new Error('Storyblok API is not initialized');
    }

    const isEditMode = await this.waitForEditMode();

    const res = await this.storyblokApi.get(
      `cdn/datasource_entries`,
      {
        datasource: 'labels',
        dimension: 'en',
        version: this.getStoryblokVersion(),
        cv: Date.now(),
      },
      this.getHeaders(isEditMode)
    );

    const labelData = res?.data.datasource_entries.find(
      (entry: IacSbDatasourceEntry) => entry.name === key
    );

    if (!labelData) {
      return key;
    }

    // const lang = this.languageService.currentLang();
    // const label = lang === 'en_global' ? labelData.dimension_value : labelData.value;

    const label = labelData.value;

    return label;
  }

  public async getBloksBySlug<Blok extends IacSbBlok = IacSbBlok>(slug: string): Promise<Blok[]> {
    return this.getBloksBySlugAndProp(slug, 'body');
  }

  public richTextResolver(): IacRichtextResolver {
    return richTextResolver();
  }

  public async getBloksBySlugAndProp<Blok extends IacSbBlok = IacSbBlok>(
    slug: string,
    prop: string
  ): Promise<Blok[]> {
    const story = await this.fetchStory(slug);
    return story.content[prop];
  }

  public setCurrentStory(story: ISbStoryData | null): void {
    this.currentStory$$.next(story);
  }

  public async getDataSource(name: string, dimension: string): Promise<IacSbDatasourceEntry[]> {
    if (this.storyblokApi === undefined) {
      throw new Error('Storyblok API is not initialized');
    }

    const isEditMode = await this.waitForEditMode();

    const res = await this.storyblokApi.get(
      `/cdn/datasource_entries`,
      {
        datasource: name,
        dimension: dimension,
        version: this.getStoryblokVersion(),
        per_page: 1000,
        cv: Date.now(),
      },
      this.getHeaders(isEditMode)
    );

    return res?.data.datasource_entries || [];
  }
}
